import React, { useContext, useEffect, useState } from 'react';
import {BrowserRouter, Navigate, Route} from "react-router-dom";
import AppRouter from "./components/AppRouter";
import NavBar from "./components/NavBar";
import { observer } from "mobx-react-lite";
import { check } from "./http/userAPI";
import { Context } from "./index";
import {toast, ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "./components/Loader";
import {getAffiliateRefIdByAliasApi} from "./http/affiliateAPI";
import VisitLogPixel from "./components/VisitLogPixel";
import axios from "axios";
import PublicFooter from "./components/PublicFooter";
import ScrollToTop from "./components/ScrollToTop";
import GlobalSpaUpdateNotice from "./helpers/GlobalSpaUpdateNotice";
// один раз показываем тост
let sessionToastShown = false;

function showSessionExpiredToast(isAuth) {
    if (!isAuth) return;                 // ❗ Только авторизованным
    if (sessionToastShown) return;
    sessionToastShown = true;

    toast.error(
        <div className="d-flex align-items-center justify-content-between">
            <div>
                <div className="fw-semibold mb-0">Session expired</div>
                <span className="small">Please reload the page to continue.</span>
            </div>

            <button
                className="btn btn-sm btn-success ms-3"
                style={{ whiteSpace: "nowrap" }}
                onClick={() => window.location.reload()}
            >
                Reload
            </button>
        </div>,
        {
            autoClose: false,
            closeOnClick: false,
            draggable: false,
            toastId: "session-expired",
        }
    );
}

let currentUserIsAuth = false;

const consumeTokenFromUrlHash = () => {
    if (!window.location.hash) return null;

    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const token = hashParams.get("vl_token");
    if (!token) return null;

    localStorage.setItem("token", token);
    hashParams.delete("vl_token");

    const cleanHash = hashParams.toString();
    const cleanUrl = `${window.location.pathname}${window.location.search}${cleanHash ? `#${cleanHash}` : ""}`;
    window.history.replaceState(null, "", cleanUrl);

    return token;
};
// глобальный перехватчик 401
axios.interceptors.response.use(
    (res) => res,
    (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        const isAuthEndpoint = /\/auth\/(login|refresh|logout)/i.test(url);

        if (status === 401 && !isAuthEndpoint) {
            showSessionExpiredToast(currentUserIsAuth);
        }
        return Promise.reject(error);
    }
);




const App = observer(() => {
    const { user, affiliateStore } = useContext(Context);
    const [loading, setLoading] = useState(true);
    const [scale, setScale] = useState(1);

    useEffect(() => {
        const tryResolveAlias = async () => {
            const pathname = window.location.pathname;
            const queryRef = new URLSearchParams(window.location.search).get("ref");
            if (queryRef) {
                localStorage.setItem("ref", queryRef);
                return;
            }

            // если маршрут уже стандартный — выходим
            const reservedPaths = [
                "", "login", "library", "account", "pay", "admin", "settings",
                "dashboard", "reset_password", "set_new_password", "terms_of_use",
                "privacy_policy", "amazon-bsrCalc-calculator", "amazon-suggestions-extractor",
                "add-funds", "return", "return-add-funds", "pricing", "survey_library", "promotion", "verify-email",
                "email-unsubscribe",
            ];
            const cleaned = pathname.replace(/^\//, "").toLowerCase();

            if (reservedPaths.includes(cleaned)) return;

            try {
                const data = await getAffiliateRefIdByAliasApi(cleaned);
                if (data.userId) {
                    localStorage.setItem("ref", data.userId);
                    affiliateStore.setReferrerData(data)
                 }
            } catch (e) {
                console.warn("Alias not found or failed to fetch:", e);
            }
        };

        tryResolveAlias();
    }, []);


    // ✅ Проверка токена при инициализации
    useEffect(() => {
        const initializeAuth = async () => {
            const token = consumeTokenFromUrlHash() || localStorage.getItem('token');
            if (token) {
                try {
                    const data = await check();
                    user.setUser(data);
                    user.setIsAuth(true);
                    currentUserIsAuth = user.isAuth;
                } catch (error) {
                    console.error("Token validation failed:", error.message);
                    localStorage.removeItem('token');
                    user.setUser({});
                    user.setIsAuth(false);
                }
            } else {
                user.setUser({});
                user.setIsAuth(false);
                currentUserIsAuth = user.isAuth;
            }
            setLoading(false);
        };
        initializeAuth();
    }, []);

    // ✅ Авто-обновление данных раз в 5 минут
    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                const data = await check();
                user.setUser(data);
            } catch (error) {
                console.error("Error refreshing user data", error);
                if (error?.response?.status === 401) {
                    showSessionExpiredToast();
                }
            }
        }, 300000); // 5 минут

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const baseWidth = 1600;
        const updateScale = () => {
            const currentWidth = window.innerWidth;
            if (currentWidth < 992) {
                setScale(1); // Не масштабируем, включаем мобильную верстку
            } else {
                const newScale = currentWidth / baseWidth;

                // Обновляем переменную для масштаба
                document.documentElement.style.setProperty('--modal-scale', newScale);

                setScale(newScale);
            }
        };

        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, []);

    if (loading) {
        return <Loader />;
    }

    return (
        <BrowserRouter>
            <ScrollToTop />
            <GlobalSpaUpdateNotice />
            <NavBar isAuth={user.isAuth} />
            {/* Масштабируемый контейнер */}
            <div
                id="main-content"
                style={{
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                    width: `${100 / scale}%`,
                marginTop: "98px"
                }}
            >
                <VisitLogPixel />

                <AppRouter />
                {/* Футер только на публичных страницах */}
                <PublicFooter />
            </div>

            {/* Toasts — вне масштаба */}
            <ToastContainer position="bottom-center" autoClose={3000} hideProgressBar={false} />
        </BrowserRouter>
    );
});

export default App;
